import { useCallback } from "react";
import { usePlateEditorState } from "@udecode/plate-core";

import { isIntentActive, doIntent, isIntentDisabled } from "./helpers";
import { Icon } from "../icon/Icon";
import {
  INTENT_BULLETED_LIST,
  INTENT_HIDE_KEYBOARD,
  INTENT_NUMBERED_LIST,
  INTENT_QUOTE,
  INTENT_REDO,
  INTENT_SHOW_KEYBOARD,
  INTENT_UNDO,
} from "./constants";

import type { MouseEvent } from "react";
import type { IconProps } from "../icon/Icon";
import type { Editor } from "./types";

export type TextEditorControlBlockIntent =
  | "bulletedList"
  | "numberedList"
  | "quote";

export type TextEditorControlMarkIntent =
  | "bold"
  | "italic"
  | "link"
  | "underline";

export type TextEditorControlBehaviorIntent =
  | "undo"
  | "redo"
  | "outdent"
  | "indent"
  | "hideKeyboard"
  | "showKeyboard";

export type TextEditorControlIntent =
  | TextEditorControlMarkIntent
  | TextEditorControlBlockIntent
  | TextEditorControlBehaviorIntent;

export interface TextEditorControlProps {
  label?: string;
  intent: TextEditorControlIntent;
  isActive?: boolean;
  intentOverride?: () => void;
}

export function getIconProps(
  intent: TextEditorControlIntent,
): [IconProps["icon"], IconProps["variant"]?] {
  switch (intent) {
    case INTENT_QUOTE:
      return ["quote-left", "solid"];
    case INTENT_UNDO:
      return ["arrow-rotate-left"];
    case INTENT_REDO:
      return ["arrow-rotate-right"];
    case INTENT_BULLETED_LIST:
      return ["list-ul"];
    case INTENT_NUMBERED_LIST:
      return ["list-ol"];
    case INTENT_HIDE_KEYBOARD:
      return ["keyboard-down"];
    case INTENT_SHOW_KEYBOARD:
      return ["keyboard-up"];
    default:
      return [intent];
  }
}

export const TextEditorControl = ({
  label,
  intent,
  intentOverride,
}: TextEditorControlProps) => {
  const editor = usePlateEditorState() as Editor;
  const isActive = isIntentActive(editor, intent);
  const isDisabled = isIntentDisabled(editor, intent);
  const [icon, iconVariant] = getIconProps(intent);

  const onMouseDown = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (intentOverride) {
        intentOverride();
      } else {
        doIntent(editor, intent);
      }
    },
    [editor, intent, intentOverride],
  );

  return (
    <button
      className="TextEditorControl"
      title={label}
      aria-label={label}
      aria-pressed={isActive}
      disabled={isDisabled}
      onMouseDown={editor && onMouseDown}
    >
      <Icon icon={icon} variant={iconVariant} size="md" />
    </button>
  );
};
